// ASME B31.8 / B31.4 pipeline wall design: hoop stress on SMYS with a design
// factor, NOMINAL wall (the mill under-tolerance is covered by the factor,
// not deducted), and any corrosion allowance added on top. Pig launchers,
// receivers and pipeline spools are built to this basis, and it lands a long
// way from the allowable-stress method (B31.3 / Section VIII) the calculator
// started with: 16" × 0.500" X52 at 1,480 psi passes here and fails there.

export const PIPELINE_CODES = ["B31.8", "B31.4"];

export const isPipelineCode = (code) => PIPELINE_CODES.includes(code);

// B31.8 Table 841.1.6-1 location classes.
export const B318_LOCATION_CLASSES = [
  { id: "1-1", label: "Class 1, Division 1" },
  { id: "1-2", label: "Class 1, Division 2" },
  { id: "2", label: "Class 2" },
  { id: "3", label: "Class 3" },
  { id: "4", label: "Class 4" },
];

// Design factor F by construction: Table 841.1.6-1 for pipelines, Table
// 841.1.6-2 for the constructions that get a tighter factor. A launcher
// is a fabricated assembly — 0.60 even in Class 1.
export const B318_CONSTRUCTIONS = [
  {
    id: "pipeline",
    label: "Pipeline, main or service line (Table 841.1.6-1)",
    factors: { "1-1": 0.8, "1-2": 0.72, 2: 0.6, 3: 0.5, 4: 0.4 },
  },
  {
    id: "assembly",
    label: "Fabricated assembly — launcher, receiver, header (Table 841.1.6-2)",
    factors: { "1-1": 0.6, "1-2": 0.6, 2: 0.6, 3: 0.5, 4: 0.4 },
  },
  {
    id: "station",
    label: "Compressor station piping (Table 841.1.6-2)",
    factors: { "1-1": 0.5, "1-2": 0.5, 2: 0.5, 3: 0.5, 4: 0.4 },
  },
];

// B31.8 Table 841.3.2-1: minimum hydrostatic test pressure as a multiple of
// MAOP, for lines operating at 30% SMYS or more.
export const B318_TEST_FACTORS = {
  "1-1": 1.25,
  "1-2": 1.1,
  2: 1.25,
  3: 1.4,
  4: 1.4,
};

// B31.4 §403.2.1: F = 0.72; §437.4.1: test at 1.25 × design pressure.
export const B314_DESIGN_FACTOR = 0.72;
export const B314_TEST_FACTOR = 1.25;

// Longitudinal joint factor E — B31.8 Table 841.1.7-1 / B31.4 Table 403.2.1-1.
export const JOINT_FACTORS = [
  { id: "seamless", label: "Seamless, ERW or SAW (E = 1.00)", value: 1 },
  {
    id: "efw",
    label: "Electric fusion welded, A134 / A139 (E = 0.80)",
    value: 0.8,
  },
  { id: "furnace", label: "Furnace butt welded (E = 0.60)", value: 0.6 },
];

export const jointFactorValue = (id) =>
  (JOINT_FACTORS.find((j) => j.id === id) || JOINT_FACTORS[0]).value;

export function b318DesignFactor(constructionId, locationClass) {
  const construction =
    B318_CONSTRUCTIONS.find((c) => c.id === constructionId) ||
    B318_CONSTRUCTIONS[0];
  return construction.factors[locationClass] ?? construction.factors["2"];
}

// B31.8 Table 841.1.8-1 temperature derating factor T, interpolated between
// rows as the table permits. Above 450°F the table ends.
const T_TABLE = [
  [250, 1],
  [300, 0.967],
  [350, 0.933],
  [400, 0.9],
  [450, 0.867],
];

export function temperatureDerating(tempF) {
  const t = Number(tempF);
  if (!Number.isFinite(t) || t <= 250) return 1;
  if (t > 450) return null;
  for (let i = 1; i < T_TABLE.length; i += 1) {
    const [t0, f0] = T_TABLE[i - 1];
    const [t1, f1] = T_TABLE[i];
    if (t <= t1) return f0 + ((f1 - f0) * (t - t0)) / (t1 - t0);
  }
  return null;
}

const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// The design formula, B31.8 §841.1.1 / B31.4 §403.2.1:
//   P = (2 · S · t / D) · F · E · T
// solved both ways. `wallThickness` is the nominal wall being checked;
// `corrosionAllowance` is added to the pressure-design wall and taken off
// the checked wall before its MAOP is worked out.
export function pipelineWall({
  code,
  outsideDiameter,
  wallThickness,
  smys,
  designPressure,
  designFactor,
  jointFactor = 1,
  temperatureFactor = 1,
  corrosionAllowance = 0,
  locationClass = "2",
}) {
  const D = num(outsideDiameter);
  const S = num(smys);
  const P = num(designPressure);
  const F = num(designFactor);
  const E = num(jointFactor);
  const T = num(temperatureFactor);
  const CA = num(corrosionAllowance) || 0;
  const t = num(wallThickness);
  if (
    !(D > 0) ||
    !(S > 0) ||
    !(P > 0) ||
    !(F > 0) ||
    !(E > 0) ||
    !(T > 0) ||
    CA < 0
  ) {
    return null;
  }

  const strength = 2 * S * F * E * T;
  const requiredPressureWall = (P * D) / strength;
  const requiredNominalWall = requiredPressureWall + CA;
  const testFactor =
    code === "B31.4" ? B314_TEST_FACTOR : B318_TEST_FACTORS[locationClass];
  const minTestPressure = P * (testFactor || B314_TEST_FACTOR);

  const out = {
    code,
    designFactor: F,
    jointFactor: E,
    temperatureFactor: T,
    corrosionAllowance: CA,
    requiredPressureWall,
    requiredNominalWall,
    testFactor: testFactor || B314_TEST_FACTOR,
    minTestPressure,
    maop: null,
    hoopStress: null,
    hoopStressPctSmys: null,
    testHoopStressPctSmys: null,
    adequate: null,
    margin: null,
  };

  if (t > 0) {
    const effective = Math.max(t - CA, 0);
    out.maop = (strength * effective) / D;
    // Operating hoop stress on the nominal wall, the way the code figures it.
    out.hoopStress = (P * D) / (2 * t);
    out.hoopStressPctSmys = (out.hoopStress / S) * 100;
    out.testHoopStressPctSmys = ((minTestPressure * D) / (2 * t) / S) * 100;
    out.adequate = t + 1e-9 >= requiredNominalWall;
    out.margin = out.maop / P - 1;
  }
  return out;
}
